const env = process.env.REACT_APP_ENV

// Apple App Store numeric id for WashXpress (bundle com.laundryyou.washxpress).
// One listing across all envs. Verified via the iTunes Lookup API:
// https://apps.apple.com/th/app/washxpress/id1577335601
const APPLE_APP_ID = '1577335601'

// Native app flavors (must match mobile-washxpress android/app/build.gradle
// productFlavors + iOS URI_SCHEME). Each flavor has its own applicationId and
// URL scheme, so the web must target the flavor matching its own env.
// iOS has a single App Store listing (prod), so appStoreUrl is shared.
type AppFlavor = 'dev' | 'staging' | 'uat' | 'prod'

const makeAppTarget = (
  idSuffix: string,
  scheme: string,
  universalLink: string,
  // AppGallery app_id from mobile-washxpress android/app/src/<flavor>/agconnect-services.json
  appGalleryId: string,
) => {
  const androidPackage = `com.laundryyou.washxpress${idSuffix}`
  return {
    appleAppId: APPLE_APP_ID,
    appStoreUrl: `https://apps.apple.com/app/id${APPLE_APP_ID}`,
    androidPackage,
    playStoreUrl: `https://play.google.com/store/apps/details?id=${androidPackage}`,
    appGalleryUrl: `https://appgallery.huawei.com/app/C${appGalleryId}`,
    universalLink,
    urlScheme: `${scheme}://`,
  }
}

const appTargets: Record<AppFlavor, ReturnType<typeof makeAppTarget>> = {
  dev: makeAppTarget(
    '.dev',
    'washxpress-dev',
    'staging.app.washxpressth.com',
    '107485131',
  ),
  staging: makeAppTarget(
    '.staging',
    'washxpress-stg',
    'staging.app.washxpressth.com',
    '119462751',
  ),
  uat: makeAppTarget(
    '.uat',
    'washxpress-uat',
    'uat.app.washxpressth.com',
    '107311931',
  ),
  prod: makeAppTarget('', 'washxpress', 'app.washxpressth.com', '107311921'),
}

// Pick the flavor from the web host first (same rule as NetSummaryPage), then
// the build env. The browser can't detect which flavor is installed, so the
// web env decides; if that flavor isn't installed we fall back to the store.
const resolveAppFlavor = (): AppFlavor => {
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  if (host === 'app.washxpressth.com') return 'prod'
  if (host.startsWith('uat.')) return 'uat'
  if (host.startsWith('staging.')) return 'staging'
  if (env === 'production') return 'prod'
  if (env === 'uat') return 'uat'
  if (env === 'staging') return 'staging'
  return 'dev'
}

export const appFlavor = resolveAppFlavor()
const appTarget = appTargets[appFlavor]

// Some flavors are interchangeable: staging and dev point at the same backend,
// so if the primary flavor's app isn't installed we try the other before the
// store. Keyed by primary flavor → ordered fallback flavors.
const fallbackFlavors: Partial<Record<AppFlavor, AppFlavor[]>> = {
  staging: ['dev'],
}

const defaultConfig = {
  uniqueCode: 'j3ygj',
  // apiPath: 'https://staging.app.washxpressth.com/api',
  //apiPath: 'https://stgapi.washxpressth.com',
  apiPath: 'http://localhost:3001',
  omise_public_key: 'pkey_test_5o2atdmwwubmdebbh6q',
  google_map_key: 'AIzaSyBsVtmN36idDDJy5RnSxZ6DuNRpOlmkgRA',
}

const productionConfig = {
  apiPath: 'https://app.washxpressth.com/api',
  omise_public_key: 'pkey_5oo7ma2n3g38cjoingk',
}

const stagingConfig = {
  // apiPath: 'https://staging.app.washxpressth.com/api',
  apiPath: 'https://stgapi.washxpressth.com',
}

const config = {
  ...defaultConfig,
  ...(env === 'production' ? productionConfig : {}),
  ...(env === 'staging' ? stagingConfig : {}),
  // app targets picked by the single rule above: prod-only prod app, else dev
  ...appTarget,
}

// Dev server only: route API calls through Vite's proxy (relative path) so the
// browser treats them as same-origin. This avoids CORS when the app is opened
// from a tunnel host (e.g. devtunnels.ms) that the backend doesn't allowlist.
// Production/preview builds keep the absolute apiPath above untouched.
if (import.meta.env.DEV) {
  config.apiPath = env === 'production' ? '/api' : '/stgapi'
}

// Store URL for the current platform, used as the fallback when the app
// scheme can't be opened (app not installed).
// Huawei UA (Huawei Browser / older UAs that still name the vendor) → AppGallery.
// Chrome's reduced UA hides the vendor, so this is only a hint; the Android
// intent below doesn't rely on it (see openAppOrStore). The https AppGallery
// link is used because intent fallbacks must be http(s) — `appmarket://`
// isn't accepted there.
// ponytail: UA sniff, misses Huawei on Chrome; market:// resolution covers that.
export const isHuaweiDevice = (): boolean =>
  /Huawei|HMSCore|HuaweiBrowser/i.test(navigator.userAgent || '')

export const getStoreUrl = (): string => {
  const ua = navigator.userAgent || ''
  if (/iPad|iPhone|iPod/.test(ua)) return config.appStoreUrl
  return isHuaweiDevice() ? config.appGalleryUrl : config.playStoreUrl
}

// Store URL for the PROD app on the current platform. This is the final
// fallback for every web env: when no flavor in the chain opens, we always
// send the user to the public prod store listing (dev/staging/uat listings
// aren't public), per product decision.
export const getProdStoreUrl = (): string => {
  const ua = navigator.userAgent || ''
  const prod = appTargets.prod
  if (/iPad|iPhone|iPod/.test(ua)) return prod.appStoreUrl
  return isHuaweiDevice() ? prod.appGalleryUrl : prod.playStoreUrl
}

// Flavor chain for a web env: primary + interchangeable fallbacks
// (e.g. staging → dev, same backend). When none opens, we go to the prod store.
const flavorChainFor = (f: AppFlavor): AppFlavor[] => [
  f,
  ...(fallbackFlavors[f] ?? []),
]

// Walk the flavor chain one scheme at a time; if none opens, go to the prod
// store. Same mechanism on iOS and Android: navigate to the flavor's custom
// scheme and start a timer.
//  - App installed  → it opens, the page is backgrounded, visibilitychange/
//    pagehide fires and cancels the timer. Done on the first flavor that has
//    an app, so with both staging+dev installed staging (chain[0]) wins.
//  - App missing     → the page stays visible, the timer fires, and we try the
//    next flavor, or the prod store after the last one.
//
// We deliberately avoid intent:// with S.browser_fallback_url: Chrome treats a
// same-origin https fallback as "navigate there now" and skips opening the app,
// so staging would never launch even when installed. The plain custom scheme
// lets Chrome open the app and only our timer decides the fallback. (Chrome may
// show a one-time "open in app?" confirm; that's a Chrome behavior, not ours.)
const openFlavorChain = (chain: AppFlavor[], i: number, host: string): void => {
  const ua = navigator.userAgent || ''
  const isIOS = /iPad|iPhone|iPod/.test(ua)
  const isAndroid = /Android/.test(ua)
  // Every env's final fallback is the PROD store (per product decision), not
  // the current flavor's store.
  const storeUrl = getProdStoreUrl()
  // App intent-filter is scheme=<flavor scheme>, host=open, pathPrefix="/"
  const path = host.includes('/') ? host : `${host}/`

  if (!isAndroid && !isIOS) {
    window.location.href = storeUrl // desktop/unknown: no app, prod store
    return
  }

  const target = appTargets[chain[i]]
  const last = i >= chain.length - 1

  let timer: ReturnType<typeof setTimeout> | undefined
  const cancel = () => {
    if (timer !== undefined) {
      clearTimeout(timer)
      timer = undefined
    }
    document.removeEventListener('visibilitychange', onHidden)
    window.removeEventListener('pagehide', cancel)
  }
  const onHidden = () => {
    if (document.visibilityState === 'hidden') cancel()
  }
  document.addEventListener('visibilitychange', onHidden)
  window.addEventListener('pagehide', cancel)

  // Navigate to the flavor's custom scheme (user-gesture initiated).
  window.location.href = `${target.urlScheme}${path}`

  timer = setTimeout(() => {
    cancel()
    if (!last) {
      openFlavorChain(chain, i + 1, host)
      return
    }
    // Last flavor's app is missing → prod store (prod App Store on iOS,
    // prod AppGallery on Huawei, prod Play Store otherwise).
    window.location.href = storeUrl
  }, 1500)
}

// Open the native app, falling back through interchangeable flavors then the
// store when it isn't installed. See openFlavorChain for the mechanics.
export const openAppOrStore = (host = 'open'): void => {
  openFlavorChain(flavorChainFor(appFlavor), 0, host)
}

export default config
